import { useState, useMemo, useRef, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Check, AlertCircle } from 'lucide-react';
import { InlineWidget } from 'react-calendly';
import { logError } from '../../lib/logger';
import { CALENDLY_URL } from '../../constants';

const API = process.env.REACT_APP_BACKEND_URL;

const CALENDLY_UTM = { utmSource: 'website', utmMedium: 'contact-section', utmCampaign: 'analytics-assessment' };
const CALENDLY_STYLES = { height: '640px', minWidth: '280px', width: '100%' };
const CALENDLY_PAGE_SETTINGS = { hideEventTypeDetails: false, hideLandingPageDetails: false };

const INITIAL = { name: '', email: '', company: '', service: '', budget: '', message: '', hp_field: '' };

const SERVICES = [
  'Business Intelligence Dashboards',
  'Data Visualization',
  'Predictive Analytics',
  'Marketing Analytics',
  'Financial Reporting',
  'Data Strategy Consulting',
  'Other / Not sure yet',
];
const BUDGETS = ['Under $5,000', '$5,000 - $10,000', '$10,000 - $25,000', '$25,000 - $50,000', '$50,000+', 'Prefer to discuss'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const errStyle = { color: '#dc2626', fontSize: '.76rem', fontWeight: 600 };
const optStyle = { fontWeight: 400, color: 'var(--muted)' };

export const ContactBand = ({ bookingPlacement = 'contact_section' }) => {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');
  const [calendlyFailed, setCalendlyFailed] = useState(false);
  const mountedAt = useRef(Date.now());
  const embedRef = useRef(null);

  const upd = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => (p[name] ? { ...p, [name]: '' } : p));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Please enter your name.';
    if (!form.email.trim()) errs.email = 'Please enter your email.';
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = 'Please enter a valid email address.';
    if (!form.company.trim()) errs.company = 'Please enter your company.';
    if (!form.service) errs.service = 'Please choose a service.';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return; // ignore repeat clicks
    setApiError('');

    // Honeypot — real users never fill this. Silent drop.
    if (form.hp_field.trim()) return;

    // Timing — reject implausibly fast (bot) submissions.
    const elapsed = Date.now() - mountedAt.current;
    if (elapsed < 3000) {
      setErrors({ form: 'Please take a moment to complete the form.' });
      return;
    }

    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          service: form.service,
          budget: form.budget,
          message: form.message.trim(),
          hp: form.hp_field,
          elapsed_ms: elapsed,
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);

      // GTM owns booking/conversion tracking. On a confirmed server success the app
      // pushes exactly one non-PII lead signal; GTM maps generate_lead -> GA4 + Google Ads.
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'generate_lead', lead_service: form.service || 'none', lead_budget: form.budget || 'none' });
      } catch (err) { /* no-op */ }

      // HubSpot (free plan): associate the tracking cookie with this contact. No PII in GA4.
      try {
        window._hsq = window._hsq || [];
        window._hsq.push(['identify', { email: form.email.trim(), firstname: form.name.trim(), company: form.company.trim() }]);
        window._hsq.push(['trackPageView']);
      } catch (err) { /* no-op */ }

      setSubmitted(true);
    } catch (err) {
      logError('Contact send failed', err);
      setApiError("Something went wrong — email us at fsoh@pnielanalytics.com and we'll get right back to you.");
    }
    setSubmitting(false);
  };

  // Fallback: if the inline Calendly widget hasn't injected an iframe within ~8s, show a direct link.
  useEffect(() => {
    if (!submitted) return undefined;
    const t = setTimeout(() => {
      if (!embedRef.current || !embedRef.current.querySelector('iframe')) {
        setCalendlyFailed(true);
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [submitted]);

  const prefill = useMemo(() => ({
    name: form.name,
    email: form.email,
    customAnswers: { a1: form.service, a2: form.budget },
  }), [form.name, form.email, form.service, form.budget]);

  const fallbackUrl = `${CALENDLY_URL}?name=${encodeURIComponent(form.name)}&email=${encodeURIComponent(form.email)}`;

  return (
    <section className="section services" id="contact">
      <div className="container">
        <div className="heading" style={{ maxWidth: 'none', textAlign: 'center', margin: '0 auto 26px' }}>
          <p className="eyebrow">Get in touch</p>
          <h2>Book your analytics assessment</h2>
          <p>Tell us a little about your project, then grab a time that works, whichever suits you.</p>
          <div className="reply-pill" data-testid="contact-reply-commitment">
            <Clock className="w-4 h-4" /> We reply within one business day
          </div>
        </div>

        <div className="contact-panel">
          <div className="contact-card">
            {submitted ? (
              <div data-testid="contact-scheduler">
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                  <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#dcfce7', color: '#16a34a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                    <Check className="w-6 h-6" />
                  </div>
                  <p className="calendly-note" data-testid="contact-booking-copy" style={{ fontSize: '1rem', color: 'var(--ink)', fontWeight: 600 }}>
                    Thanks &mdash; we have your details. Grab a time now, or we&rsquo;ll reply within one business day.
                  </p>
                </div>

                {calendlyFailed ? (
                  <div style={{ textAlign: 'center', padding: '12px 0' }} data-testid="contact-calendly-fallback">
                    <a className="button" href={fallbackUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-calendly-fallback-btn">
                      Book a time
                    </a>
                    <p className="calendly-note" style={{ marginTop: 12 }}>We&rsquo;ll email you within one business day.</p>
                  </div>
                ) : (
                  <div ref={embedRef} data-testid="contact-calendly-embed">
                    <InlineWidget url={CALENDLY_URL} prefill={prefill} utm={CALENDLY_UTM} styles={CALENDLY_STYLES} pageSettings={CALENDLY_PAGE_SETTINGS} />
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="contact-form">
                {(apiError || errors.form) && (
                  <div role="alert" data-testid="contact-form-error" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: '.86rem' }}>
                    <AlertCircle className="w-4 h-4" style={{ flexShrink: 0, marginTop: 2 }} /> <span>{apiError || errors.form}</span>
                  </div>
                )}

                {/* Honeypot — visually hidden, off-screen, not announced to AT. Obscure name avoids autofill. */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
                  <label htmlFor="cb-extra-field">Leave this field empty</label>
                  <input id="cb-extra-field" type="text" name="hp_field" value={form.hp_field} onChange={upd} tabIndex={-1} autoComplete="off" />
                </div>

                <div className="form-grid">
                  <div className="field">
                    <label htmlFor="cb-name">Name</label>
                    <input id="cb-name" name="name" value={form.name} onChange={upd} required aria-required="true" aria-invalid={!!errors.name} data-testid="contact-name-input" />
                    {errors.name && <span role="alert" style={errStyle} data-testid="contact-name-error">{errors.name}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="cb-email">Work email</label>
                    <input id="cb-email" type="email" name="email" value={form.email} onChange={upd} required aria-required="true" aria-invalid={!!errors.email} data-testid="contact-email-input" />
                    {errors.email && <span role="alert" style={errStyle} data-testid="contact-email-error">{errors.email}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="cb-company">Company</label>
                    <input id="cb-company" name="company" value={form.company} onChange={upd} required aria-required="true" aria-invalid={!!errors.company} data-testid="contact-company-input" />
                    {errors.company && <span role="alert" style={errStyle} data-testid="contact-company-error">{errors.company}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="cb-service">Service interested in</label>
                    <select id="cb-service" name="service" value={form.service} onChange={upd} required aria-required="true" aria-invalid={!!errors.service} data-testid="contact-service-select">
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {errors.service && <span role="alert" style={errStyle} data-testid="contact-service-error">{errors.service}</span>}
                  </div>
                  <div className="field full">
                    <label htmlFor="cb-budget">Budget range <span style={optStyle}>(optional)</span></label>
                    <select id="cb-budget" name="budget" value={form.budget} onChange={upd} data-testid="contact-budget-select">
                      <option value="">Select a range</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="field full">
                    <label htmlFor="cb-message">Anything we should know? <span style={optStyle}>(optional)</span></label>
                    <textarea id="cb-message" name="message" value={form.message} onChange={upd} data-testid="contact-message-input" />
                  </div>
                </div>

                <button className="button" type="submit" disabled={submitting} data-testid="contact-submit-btn" style={{ marginTop: 16, width: '100%' }}>
                  {submitting ? 'Sending…' : 'Continue to booking'}
                </button>
                <p style={{ fontSize: '.82rem', color: 'var(--muted)', textAlign: 'center', marginTop: 10 }}>
                  <Clock className="w-3.5 h-3.5" style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} /> We reply within one business day.
                </p>
              </form>
            )}
          </div>
        </div>

        <div className="contact-info" data-testid="contact-info">
          <div className="info-row">
            <div className="info-ic"><Mail className="w-5 h-5" /></div>
            <div><h3>Email</h3><p>info@pnielanalytics.com</p></div>
          </div>
          <div className="info-row">
            <div className="info-ic"><Phone className="w-5 h-5" /></div>
            <div><h3>Phone</h3><p>+1 302 455 7460</p></div>
          </div>
          <div className="info-row">
            <div className="info-ic"><MapPin className="w-5 h-5" /></div>
            <div><h3>Office</h3><p>Newark, Delaware</p></div>
          </div>
        </div>
      </div>
    </section>
  );
};
